import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogIn, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { admin, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Admin Login · Peak Plumbing";
  }, []);

  useEffect(() => {
    if (admin && typeof admin === "object") {
      navigate("/admin/leads", { replace: true });
    }
  }, [admin, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate("/admin/leads", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center bg-slate-50 px-6"
    >
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-md p-8 shadow-sm">
        <div className="flex items-center gap-2 text-orange-600 text-xs font-bold uppercase tracking-widest">
          <ShieldCheck size={14} /> Restricted area
        </div>
        <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mt-2">
          Admin Login
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Sign in to view submitted leads for Peak Plumbing.
        </p>

        <form onSubmit={onSubmit} className="mt-6 grid gap-4" noValidate>
          <label className="grid gap-1">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
              Email
            </span>
            <input
              data-testid="admin-login-email"
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="you@peakplumbingmi.com"
            />
          </label>

          <label className="grid gap-1">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
              Password
            </span>
            <input
              data-testid="admin-login-password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="••••••••"
            />
          </label>

          {error && (
            <p
              data-testid="admin-login-error"
              className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm px-3 py-2"
            >
              {error}
            </p>
          )}

          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-bold text-sm uppercase tracking-wider px-4 py-2.5 rounded-sm transition-colors"
          >
            <LogIn size={15} />
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </main>
  );
}
